import { makeAutoObservable, runInAction } from 'mobx';
import { showToast } from '../../../utils/toast';
import { companyListURL, companyURL, countryListURL, deleteCompanyById } from '../api/url';
import { COMPANY_FORM_INITIAL } from '../constants/company.constants';
import { authStore } from '../../Auth';
import { SYSTEM_ADMIN } from '../../../constants/constants';
import { handleFetch } from '../../../utils/handleFetch';

class CompanyStore {
	companies = [];
	totalCompanies = 0;
	companyForm = COMPANY_FORM_INITIAL;
	page = 1;
	limit = 12;
	errors = {};
	search = '';
	isCompanyModalOpen = false;
	isDeleteConfirmModalOpen = false;
	selectedCompanyId = null;
	selectedDeleteCompanyId = null;
	selectedCompanyName = '';
	isBatchUploadModalOpen = false;
	countryList = [];

	constructor() {
		makeAutoObservable(this);
	}

	setIsBatchUploadModalOpen = (v) => (this.isBatchUploadModalOpen = v);

	setSelectedCompanyId = (v) => (this.selectedCompanyId = v);

	openCompanyModal = () => (this.isCompanyModalOpen = true);

	closeCompanyModal = () => {
		this.companyForm = COMPANY_FORM_INITIAL;
		this.isCompanyModalOpen = false;
		this.errors = {};
	};

	openDeleteConfirmModal = (companyId, companyName) => {
		this.selectedDeleteCompanyId = companyId;
		this.selectedCompanyName = companyName;
		this.isDeleteConfirmModalOpen = true;
	};

	closeDeleteConfirmModal = () => {
		this.isDeleteConfirmModalOpen = false;
	};

	setSearch = (v) => (this.search = v);

	setCompanyForm = (id) => {
		const company = this.companies.find((c) => c._id === id);
		this.companyForm = { ...company } || COMPANY_FORM_INITIAL;
	};

	setCompanyFormValue = (e) => {
		const { id, value } = e.target;
		this.companyForm[id] = value;
	};

	setPage = (v) => (this.page = parseInt(v));

	getCompanies = async () => {
		const url = companyURL() + `?page=${this.page}&limit=${this.limit}&search=${this.search}`;
		return await handleFetch({
			url,
			method: 'GET',
			onSuccess: (data) => {
				runInAction(() => {
					const { total, companies } = data;
					this.totalCompanies = total;
					this.companies = companies;
				});
			},
		});
	};

	getCompanyList = async () => {
		return await handleFetch({
			url: companyListURL,
			method: 'GET',
			onSuccess: (data, message) => runInAction(() => (this.companyList = data)),
		});
	};

	saveCompany = async (level = 'parent') => {
		const userRole = authStore.role;
		const { _id, companyName, country, address, businessNo, companyType, industrySector, cLevel } = this.companyForm;
		const body = {
			_id,
			companyName,
			country,
			address,
			businessNo,
			companyType,
			industrySector,
			cLevel,
		};
		const url = _id ? companyURL(_id) : companyURL(userRole === SYSTEM_ADMIN ? 'resellerCom' : level);

		return showToast.promise(
			(async () => {
				const success = await handleFetch({
					url,
					method: _id ? 'PUT' : 'POST',
					body,
					onSuccess: (data) => {
						runInAction(() => {
							this.errors = {};
							const index = this.companies.findIndex((company) => company._id === data?._id);
							if (index > -1) {
								this.companies[index] = data;
							} else {
								if (this.page === 1) {
									if (this.companies.length === this.limit) {
										this.companies.pop();
									}
									this.companies.unshift(data);
								}
								this.totalCompanies++;
							}
							this.companyList.push({ key: data?._id, value: data?.companyName });
							this.companyForm = COMPANY_FORM_INITIAL;
						});
					},
					onValidationError: (errors) => runInAction(() => (this.errors = errors)),
				});
				if (!success) throw new Error('Failed to save company');
				return success;
			})(),
			{
				loading: _id ? 'Updating company...' : 'Creating company...',
				success: _id ? 'Company updated successfully!' : 'Company created successfully!',
				error: 'Failed to save company',
			},
		);
	};

	handleDeleteCompany = async () => {
		return showToast.promise(
			(async () => {
				const success = await handleFetch({
					url: deleteCompanyById(this.selectedDeleteCompanyId),
					method: 'PATCH',
					onSuccess: () => {
						runInAction(() => {
							this.isDeleteConfirmModalOpen = false;
							this.getCompanies();
							this.selectedDeleteCompanyId = null;
						});
					},
				});
				if (!success) throw new Error('Failed to delete company');
				return success;
			})(),
			{
				loading: 'Deleting company...',
				success: 'Company deleted successfully!',
				error: 'Failed to delete company',
			},
		);
	};

	getCountryList = async () => {
		return await handleFetch({
			url: countryListURL,
			method: 'GET',
			onSuccess: (data) => runInAction(() => (this.countryList = data)),
		});
	};
}

export const companyStore = new CompanyStore();
